"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from "react"
import {
  AUTH_LOGOUT_ENDPOINT,
  AUTH_SESSION_ENDPOINT,
  isAuthProviderName,
} from "./constants"
import type { AuthContextValue, AuthSessionSnapshot } from "./types"

const EMPTY_SESSION: AuthSessionSnapshot = {
  authenticated: false,
  provider: null,
  customer: null,
  expiresAt: null,
}

const AUTH_FOCUS_REVALIDATE_INTERVAL_MS = 60_000

const AuthContext = createContext<AuthContextValue | null>(null)

async function fetchSessionSnapshot(
  endpoint = AUTH_SESSION_ENDPOINT
): Promise<AuthSessionSnapshot> {
  const response = await fetch(endpoint, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    headers: {
      accept: "application/json",
    },
  })

  const data = (await response.json().catch(() => null)) as {
    authenticated?: boolean
    provider?: string | null
    customer?: AuthSessionSnapshot["customer"]
    expiresAt?: number | null
  } | null

  if (!response.ok) {
    const message =
      data &&
      typeof data === "object" &&
      typeof (data as any).message === "string"
        ? (data as any).message
        : "auth_session_unavailable"
    throw new Error(message)
  }

  return {
    authenticated: !!data?.authenticated,
    provider: isAuthProviderName(data?.provider) ? data.provider : null,
    customer: data?.customer ?? null,
    expiresAt: typeof data?.expiresAt === "number" ? data.expiresAt : null,
  }
}

interface AuthProviderProps {
  children: ReactNode
  /**
   * Optional SSR hint: when the server has already inspected the auth cookie
   * we can hydrate the provider in "authenticated" or "unauthenticated" state
   * instead of "loading". This eliminates the brief "login" flash on hard
   * reloads / deep links. Customer details still arrive via refresh().
   */
  initialStatus?: "authenticated" | "unauthenticated"
}

export function AuthProvider({ children, initialStatus }: AuthProviderProps) {
  const initialSession: AuthSessionSnapshot =
    initialStatus === "authenticated"
      ? { authenticated: true, provider: null, customer: null, expiresAt: null }
      : EMPTY_SESSION
  const [session, setSession] = useState<AuthSessionSnapshot>(initialSession)
  const [status, setStatus] = useState<AuthContextValue["status"]>(
    initialStatus ?? "loading"
  )
  const [error, setError] = useState<string | null>(null)
  const sessionRef = useRef(session)
  const statusRef = useRef(status)
  const lastRefreshAtRef = useRef(0)

  useEffect(() => {
    sessionRef.current = session
  }, [session])

  useEffect(() => {
    statusRef.current = status
  }, [status])

  const refresh = useCallback(async (options?: { silent?: boolean }) => {
    try {
      setError(null)
      const shouldShowLoading =
        !options?.silent && statusRef.current === "loading"
      if (shouldShowLoading) setStatus("loading")
      const nextSession = await fetchSessionSnapshot()
      setSession(nextSession)
      setStatus(nextSession.authenticated ? "authenticated" : "unauthenticated")
      lastRefreshAtRef.current = Date.now()
      return nextSession
    } catch (refreshError) {
      const message =
        refreshError instanceof Error
          ? refreshError.message
          : "auth_refresh_failed"
      setError(message)
      if (statusRef.current === "loading") setStatus("error")
      return sessionRef.current
    }
  }, [])

  const logout = useCallback(async (options?: { redirectTo?: string }) => {
    try {
      await fetch(AUTH_LOGOUT_ENDPOINT, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          "content-type": "application/json",
        },
      })
    } finally {
      setSession(EMPTY_SESSION)
      setStatus("unauthenticated")
      setError(null)

      if (options?.redirectTo && typeof window !== "undefined") {
        window.location.assign(options.redirectTo)
      }
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    const revalidate = () => {
      if (document.visibilityState !== "visible") return
      const now = Date.now()
      if (now - lastRefreshAtRef.current < AUTH_FOCUS_REVALIDATE_INTERVAL_MS)
        return
      void refresh({ silent: true })
    }

    window.addEventListener("focus", revalidate)
    document.addEventListener("visibilitychange", revalidate)

    return () => {
      window.removeEventListener("focus", revalidate)
      document.removeEventListener("visibilitychange", revalidate)
    }
  }, [refresh])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...session,
      status,
      error,
      refresh,
      logout,
    }),
    [error, logout, refresh, session, status]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider")
  }

  return context
}

export async function requireLoginAndRun<T>(
  action: () => Promise<T>,
  options?: { loginUrl?: string }
): Promise<T | null> {
  if (typeof window === "undefined") {
    throw new Error("requireLoginAndRun must be used in the browser")
  }

  const session = await fetchSessionSnapshot()
  if (session.authenticated) {
    return action()
  }

  if (options?.loginUrl) {
    window.location.assign(options.loginUrl)
  }

  return null
}
